import React, { useState } from 'react';
import { Mail, CheckCircle2 } from 'lucide-react';
import { ThemeMode } from '../types';
import { useI18n } from '../i18n/I18nContext';

interface NewsletterSectionProps {
  theme: ThemeMode;
}

export const NewsletterSection: React.FC<NewsletterSectionProps> = ({ theme }) => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const isDark = theme === 'dark';
  const { t } = useI18n();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
      setTimeout(() => {
        setSubscribed(false);
        setEmail('');
      }, 4000);
    }
  };

  return (
    <section className="py-3 sm:py-8 px-3 sm:px-6 lg:px-8 max-w-7xl mx-auto z-10 relative">
      <div className={`p-3.5 sm:p-8 rounded-xl sm:rounded-2xl border transition-all duration-300 ${
        isDark
          ? 'bg-[#0f121e]/90 border-slate-800/90 shadow-xl'
          : 'bg-white border-slate-200/90 shadow-md shadow-slate-200/60'
      }`}>
        <div className="flex flex-col lg:flex-row items-center justify-between gap-3.5 sm:gap-6">
          
          {/* Left Title & Icon */}
          <div className="flex items-center gap-3 sm:gap-4 w-full lg:w-auto">
            <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-gradient-to-tr from-purple-500 to-indigo-600 text-white shadow-md shadow-purple-500/30 flex items-center justify-center shrink-0">
              <Mail className="w-4 h-4 sm:w-6 sm:h-6" />
            </div>
            <div>
              <h2 className={`text-base sm:text-2xl font-bold tracking-tight ${
                isDark ? 'text-white' : 'text-slate-900'
              }`}>
                {t('newsletterTitle', 'Stay Updated')}
              </h2>
              <p className={`text-[11px] sm:text-sm mt-0.5 ${
                isDark ? 'text-slate-400' : 'text-slate-600'
              }`}>
                {t('newsletterSub', 'Get notified about new tools and updates')}
              </p>
            </div>
          </div>

          {/* Right Input Form */}
          <div className="w-full lg:w-auto lg:min-w-[480px]">
            {subscribed ? (
              <div className="flex items-center gap-2 p-2.5 sm:p-3 bg-emerald-500/10 border border-emerald-500/30 text-emerald-500 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                <span>{t('newsletterSuccess', "Thank you for subscribing! We'll keep you updated.")}</span>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-row items-center gap-2 sm:gap-3">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('newsletterPlaceholder', 'Enter your email')}
                  className={`w-full flex-1 py-2 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl border outline-none text-xs sm:text-sm transition-all duration-200 ${
                    isDark
                      ? 'bg-slate-950/80 border-slate-800 text-white placeholder-slate-500 focus:border-purple-500'
                      : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-purple-500 focus:bg-white'
                  }`}
                />
                <button
                  type="submit"
                  className="px-3.5 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm font-semibold transition-colors duration-200 shadow-md shadow-purple-600/20 flex-shrink-0 cursor-pointer whitespace-nowrap"
                >
                  {t('newsletterSubscribe', 'Subscribe')}
                </button>
              </form>
            )}
          </div>

        </div>
      </div>
    </section>
  );
};
